import type { RichTextEditorSerializeAs } from "../RichTextEditorProps";

export type RTETokenType =
  | "plain"
  | "punctuation"
  | "tag"
  | "attr-name"
  | "attr-value"
  | "comment"
  | "key"
  | "string"
  | "number"
  | "keyword"
  | "heading"
  | "strong"
  | "emphasis"
  | "code"
  | "link"
  | "list";

export interface RTEToken {
  readonly type: RTETokenType;
  readonly value: string;
}

interface Rule {
  readonly type: RTETokenType;
  readonly pattern: RegExp;
}

const pushPlain = (tokens: RTEToken[], value: string): void => {
  if (!value) return;
  const last = tokens[tokens.length - 1];
  if (last && last.type === "plain") tokens[tokens.length - 1] = { type: "plain", value: last.value + value };
  else tokens.push({ type: "plain", value });
};

const pushToken = (tokens: RTEToken[], type: RTETokenType, value: string): void => {
  if (type === "plain") pushPlain(tokens, value);
  else tokens.push({ type, value });
};

const scan = (source: string, rules: ReadonlyArray<Rule>): RTEToken[] => {
  const tokens: RTEToken[] = [];
  let index = 0;
  while (index < source.length) {
    let matched = false;
    for (const { type, pattern } of rules) {
      pattern.lastIndex = index;
      const match = pattern.exec(source);
      if (match) {
        pushToken(tokens, type, match[0]);
        index = pattern.lastIndex;
        matched = true;
        break;
      }
    }
    if (!matched) {
      pushPlain(tokens, source[index]);
      index += 1;
    }
  }
  return tokens;
};

const JSON_RULES: ReadonlyArray<Rule> = [
  { type: "key", pattern: /"(?:\\.|[^"\\])*"(?=\s*:)/y },
  { type: "string", pattern: /"(?:\\.|[^"\\])*"/y },
  { type: "number", pattern: /-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?/y },
  { type: "keyword", pattern: /\b(?:true|false|null)\b/y },
  { type: "punctuation", pattern: /[{}[\],:]/y },
];

const TAG_RULES: ReadonlyArray<Rule> = [
  { type: "tag", pattern: /<\/?/y },
  { type: "punctuation", pattern: /\/?>/y },
  { type: "tag", pattern: /[a-zA-Z][\w:-]*(?=[\s/>]|$)/y },
  { type: "attr-name", pattern: /[^\s=/>]+(?=\s*=)/y },
  { type: "punctuation", pattern: /=/y },
  { type: "attr-value", pattern: /"[^"]*"|'[^']*'/y },
  { type: "attr-value", pattern: /[^\s/>]+/y },
];

const HTML_CHUNK = /<!--[\s\S]*?-->|<\/?[a-zA-Z][^>]*>/g;

const tokenizeHtml = (value: string): RTEToken[] => {
  const tokens: RTEToken[] = [];
  let lastIndex = 0;
  for (const match of value.matchAll(HTML_CHUNK)) {
    const start = match.index ?? 0;
    pushPlain(tokens, value.slice(lastIndex, start));
    const chunk = match[0];
    if (chunk.startsWith("<!--")) tokens.push({ type: "comment", value: chunk });
    else for (const token of scan(chunk, TAG_RULES)) pushToken(tokens, token.type, token.value);
    lastIndex = start + chunk.length;
  }
  pushPlain(tokens, value.slice(lastIndex));
  return tokens;
};

const MD_INLINE_RULES: ReadonlyArray<Rule> = [
  { type: "code", pattern: /`[^`]+`/y },
  { type: "strong", pattern: /\*\*[^*]+\*\*|__[^_]+__/y },
  { type: "emphasis", pattern: /\*[^*\n]+\*|_[^_\n]+_/y },
  { type: "link", pattern: /!?\[[^\]]*\]\([^)]*\)/y },
  { type: "tag", pattern: /<\/?[a-zA-Z][^>]*>/y },
];

const HEADING_LINE = /^\s*#{1,6}\s/;
const BLOCKQUOTE_LINE = /^(\s*>\s?)(.*)$/;
const LIST_LINE = /^(\s*(?:[-*+]|\d+\.)\s)(.*)$/;

const tokenizeMarkdownLine = (line: string, tokens: RTEToken[]): void => {
  if (HEADING_LINE.test(line)) {
    tokens.push({ type: "heading", value: line });
    return;
  }
  const quote = BLOCKQUOTE_LINE.exec(line);
  if (quote) {
    tokens.push({ type: "punctuation", value: quote[1] });
    for (const token of scan(quote[2], MD_INLINE_RULES)) pushToken(tokens, token.type, token.value);
    return;
  }
  const list = LIST_LINE.exec(line);
  if (list) {
    tokens.push({ type: "list", value: list[1] });
    for (const token of scan(list[2], MD_INLINE_RULES)) pushToken(tokens, token.type, token.value);
    return;
  }
  for (const token of scan(line, MD_INLINE_RULES)) pushToken(tokens, token.type, token.value);
};

const tokenizeMarkdown = (value: string): RTEToken[] => {
  const tokens: RTEToken[] = [];
  const lines = value.split("\n");
  lines.forEach((line, index) => {
    tokenizeMarkdownLine(line, tokens);
    if (index < lines.length - 1) pushPlain(tokens, "\n");
  });
  return tokens;
};

export const highlightSerializedOutput = (value: string, serializeAs: RichTextEditorSerializeAs): RTEToken[] => {
  if (!value) return [];
  switch (serializeAs) {
    case "components":
    case "lexical-json":
      return scan(value, JSON_RULES);
    case "html":
      return tokenizeHtml(value);
    case "markdown":
      return tokenizeMarkdown(value);
    default:
      return [{ type: "plain", value }];
  }
};
